/**
 * GoogleSheetsSelection - Versión con Google Picker
 * 
 * Este componente usa Google Picker como método principal para seleccionar
 * hojas de cálculo, cumpliendo con el scope drive.file recomendado por Google.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import GoogleSheetsPicker from './GoogleSheetsPicker';
import { FileSpreadsheet, Check, Loader2, ArrowLeft, Plus, ExternalLink, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { getReturnPathAfterSheetsConnect, isCampaignSheetsContext } from '@/lib/googleSheetsFlow';
import { connectGoogleSheet, createGoogleSheet, getAvailableSheets } from '@/lib/googleSheetsApi';

interface SelectedSheet {
  sheet_id: string;
  name: string;
  url: string;
  owner?: string;
  last_modified?: string;
  status: 'available' | 'selected' | 'connecting' | 'connected';
}

const GoogleSheetsSelection: React.FC = () => {
  const { user, token, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [sheets, setSheets] = useState<SelectedSheet[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [tourState, setTourState] = useState<'initial' | 'selection_instruction' | 'selection_active' | 'confirmation' | 'completed' | null>(null);

  // ✅ NUEVO: Guardar el contexto de origen
  const [originContext, setOriginContext] = useState<string | null>(null);

  const loadExistingSheets = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const data = await getAvailableSheets(1, 50, { force: true });

      if (data.sheets && Array.isArray(data.sheets)) {
        setSheets(prev => {
          const backendSheetIds = new Set(data.sheets.map((s: any) => s.sheet_id));
          const localSheetsNotInBackend = prev.filter(s => !backendSheetIds.has(s.sheet_id) && s.status === 'available');

          const backendSheets: SelectedSheet[] = data.sheets.map((s: any) => ({
            sheet_id: s.sheet_id,
            name: s.name,
            url: s.url,
            owner: s.owner,
            last_modified: s.last_modified,
            status: 'available'
          }));

          const allSheets = [...backendSheets, ...localSheetsNotInBackend];
          return allSheets.sort((a, b) => {
            const dateA = new Date(a.last_modified || 0).getTime();
            const dateB = new Date(b.last_modified || 0).getTime();
            return dateB - dateA;
          });
        });
      }
    } catch (error) {
      console.error('Error loading sheets:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;

    const urlParams = new URLSearchParams(location.search);
    const authStatus = urlParams.get('auth');
    const context = urlParams.get('context');

    // ✅ Guardar el contexto para usarlo después
    if (context) {
      setOriginContext(context);
      localStorage.setItem('sheets_selection_context', context);
    } else {
      // Intentar recuperar del localStorage
      const savedContext = localStorage.getItem('sheets_selection_context');
      if (savedContext) {
        setOriginContext(savedContext);
      }
    }

    if (authStatus === 'retry') {
      toast.error("Permisos insuficientes. Por favor intenta conectar nuevamente.");
      navigate('/google-sheets');
      return;
    }

    // ✅ Fix: Cargar hojas si hay token, independientemente del authStatus
    if (token) {
      if (authStatus === 'success') {
        toast.success("¡Google Drive conectado! Usa el botón para seleccionar tus hojas.");
      }
      loadExistingSheets();
    } else {
      // Si no hay token, no podemos estar aquí
      navigate('/google-sheets');
    }
  }, [location, user, token, navigate, authLoading, loadExistingSheets]);

  // ✅ TOUR TRIGGER: Auto-start tour upon initialization with DOM check
  useEffect(() => {
    if (!initialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const isNewConnection = urlParams.get('auth') === 'success';
    const hasContext = !!urlParams.get('context');
    const tourKey = 'sheets_selection_tour_seen';
    const hasSeen = localStorage.getItem(tourKey) === 'true';

    // Disparar si es la primera vez (no hay flag) O si es una conexión nueva/cambio de contexto
    if (!hasSeen) {
      console.log('🔍 Detectado flujo de entrada. Iniciando escaneo de elementos para la guía...');

      const maxAttempts = 15; // Un poco más de tiempo
      let attempt = 0;

      const timerId = setInterval(() => {
        const testEl = document.querySelector('#tour-sheets-existing');
        attempt++;

        if (testEl) {
          clearInterval(timerId);
          console.log('✅ Elementos encontrados, activando la guía automáticamente.');
          setTourState('initial');
          import('./common/GoogleSheetsTour').then(({ startInitialSheetsTour }) => {
            startInitialSheetsTour(() => {
              // Solo marcar como visto definitivamente al completar
              localStorage.setItem(tourKey, 'true');
            });
          }).catch(err => console.error('🔥 Error al cargar el tour:', err));
        } else if (attempt >= maxAttempts) {
          clearInterval(timerId);
          console.warn('⚠️ No se pudo iniciar la guía: Elementos no disponibles.');
        }
      }, 300);

      return () => clearInterval(timerId);
    }
  }, [initialized, location.search]);

  const handlePickerSelection = (pickedSheets: any[]) => {
    console.log('Sheets from Picker:', pickedSheets);

    // Agregar las hojas del Picker
    const newSheets: SelectedSheet[] = pickedSheets.map(s => ({
      sheet_id: s.id,
      name: s.name,
      url: s.url || `https://docs.google.com/spreadsheets/d/${s.id}`,
      owner: 'Tú',
      status: 'available'
    }));

    // Combinar con existentes, evitando duplicados
    setSheets(prev => {
      const existingIds = new Set(prev.map(s => s.sheet_id));
      const uniqueNew = newSheets.filter(s => !existingIds.has(s.sheet_id));
      return [...uniqueNew, ...prev];
    });

    // Auto-seleccionar las nuevas
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSheets.forEach(s => newSet.add(s.sheet_id));
      return newSet;
    });

    // Recargar desde backend
    setTimeout(() => {
      loadExistingSheets();
      // Si estamos en tour inicial, pasar a instrucción de selección
      const hasSeenTour = localStorage.getItem('sheets_selection_tour_seen') === 'true';

      if (!hasSeenTour && (tourState === 'initial' || tourState === null)) {
        setTourState('selection_instruction');
        setTimeout(() => {
          import('./common/GoogleSheetsTour').then(({ startSheetSelectTour }) => {
            startSheetSelectTour(() => {
              setTourState('selection_active');
            });
          });
        }, 800);
      }
    }, 500);
  };

  const toggleSelection = (sheetId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sheetId)) {
        newSet.delete(sheetId);
      } else {
        newSet.add(sheetId);
      }
      return newSet;
    });

    // ✅ TRIGGER TOUR: Selección completada, mostrar paso final
    // Solo si el usuario ya vio y aceptó la instrucción de selección
    if (tourState === 'selection_active') {
      setTourState('confirmation');
      setTimeout(() => {
        import('./common/GoogleSheetsTour').then(({ startConnectConfirmTour }) => {
          startConnectConfirmTour(() => {
            localStorage.setItem('sheets_selection_tour_seen', 'true');
            setTourState('completed');
          });
        });
      }, 500);
    }
  };

  const selectAll = () => {
    const availableIds = sheets.filter(s => s.status !== 'connected').map(s => s.sheet_id);
    setSelectedIds(new Set(availableIds));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const connectSelectedSheets = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecciona al menos una hoja para conectar');
      return;
    }

    setConnecting(true);
    let successCount = 0;
    let errorCount = 0;
    let lastConnectedSheetId: string | null = null;

    const sheetIdsArray = Array.from(selectedIds);
    for (const sheetId of sheetIdsArray) {
      try {
        await connectGoogleSheet(sheetId);
        successCount++;
        lastConnectedSheetId = sheetId;
        setSheets(prev => prev.map(s =>
          s.sheet_id === sheetId ? { ...s, status: 'connected' } : s
        ));
      } catch (error) {
        console.error(`Error connecting sheet ${sheetId}:`, error);
        errorCount++;
      }
    }

    setConnecting(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} hoja(s) conectada(s) exitosamente`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} hoja(s) no se pudieron conectar`);
    }

    if (successCount > 0) {
      // ✅ NUEVO: Redirigir según el contexto de origen
      const context = originContext || localStorage.getItem('sheets_selection_context');

      // Limpiar el contexto guardado
      localStorage.removeItem('sheets_selection_context');

      setTimeout(() => {
        if (isCampaignSheetsContext(context)) {
          if (lastConnectedSheetId) {
            localStorage.setItem('preselected_sheet_id', lastConnectedSheetId);
          }
          navigate(getReturnPathAfterSheetsConnect(context));
        } else {
          navigate('/google-sheets');
        }
      }, 1500);
    }
  };

  const createNewSheet = async () => {
    if (!token) {
      toast.error('No estás autenticado');
      return;
    }

    setCreatingSheet(true);
    try {
      const sheetName = `Nueva Hoja - ${new Date().toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })}`;

      const data = await createGoogleSheet(sheetName);

      toast.success(`¡Hoja "${data.name}" creada exitosamente!`);

      const newSheet: SelectedSheet = {
        sheet_id: data.sheet_id,
        name: data.name,
        url: data.url,
        owner: 'Tú',
        status: 'available'
      };

      setSheets(prev => [newSheet, ...prev]);
      setSelectedIds(prev => new Set([...Array.from(prev), data.sheet_id]));

      setTourState('confirmation');
      setTimeout(() => {
        import('./common/GoogleSheetsTour').then(({ startConnectConfirmTour }) => {
          startConnectConfirmTour(() => {
            localStorage.setItem('sheets_selection_tour_seen', 'true');
            setTourState('completed');
          });
        });
      }, 1500);

      setTimeout(() => loadExistingSheets(), 500);
    } catch (error) {
      console.error('Error creating sheet:', error);
      toast.error('Error al crear la hoja. Por favor intenta nuevamente.');
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleManualTour = () => {
    setTourState('initial');
    import('./common/GoogleSheetsTour').then(({ startInitialSheetsTour }) => {
      startInitialSheetsTour(() => { });
    });
  };

  const goBack = () => {
    const context = originContext || localStorage.getItem('sheets_selection_context');

    if (isCampaignSheetsContext(context)) {
      navigate(getReturnPathAfterSheetsConnect(context));
    } else if (context === 'integrations') {
      navigate('/google-sheets');
    } else {
      navigate('/google-sheets');
    }
  };

  // Helper para texto del botón back
  const getBackButtonText = () => {
    const context = originContext || localStorage.getItem('sheets_selection_context');
    if (context === 'marketing-campaign' ||
      context === 'renewal-campaign' ||
      context === 'dynamic-campaign' ||
      context === 'campaign-builder') {
      return 'Volver a la campaña';
    } else if (context === 'integrations') {
      return 'Volver a integraciones';
    }
    return 'Volver a integraciones';
  };

  // Loading inicial
  if (!initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={goBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {getBackButtonText()}
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Seleccionar Google Sheets
          </h1>
          <p className="text-gray-600">
            Selecciona las hojas de cálculo de tu Google Drive que deseas conectar con Reveca.
          </p>
        </div>

        {/* Banner informativo */}
        <div className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] rounded-2xl shadow-xl p-6 mb-8 text-white border border-gray-100 relative overflow-hidden group">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 animate-pulse">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-1">
                ¡Super! Ya conectamos tu Google Drive
              </h2>
              <p className="text-white/90">
                Ahora elige la hoja de Google Sheet que vamos a utilizar para tus campañas.
              </p>
            </div>
            <Button
              onClick={handleManualTour}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 border-white/20 text-white hover:text-white transition-all flex items-center gap-2 px-6 h-11 rounded-xl group-hover:scale-105"
            >
              <GraduationCap className="w-5 h-5" />
              Ver Guía
            </Button>
          </div>
        </div>

        {/* Google Picker - Área principal */}
        <div id="tour-sheets-picker-card" className="bg-white rounded-2xl shadow-xl p-8 mb-8 text-center border border-gray-100">
          <div className="bg-blue-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <FileSpreadsheet className="w-10 h-10 text-blue-600" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Agregar hojas desde Google Drive
          </h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Haz clic en el botón para abrir Google Drive y seleccionar las hojas de cálculo que deseas usar.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div id="tour-sheets-existing" className="w-full sm:w-auto">
              <GoogleSheetsPicker
                buttonText="Conectar hojas existentes"
                buttonVariant="default"
                buttonSize="lg"
                className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-8 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95 font-semibold w-full sm:w-[350px] justify-center"
                onSheetsSelected={handlePickerSelection}
                multiSelect={true}
              />
            </div>

            <div className="flex items-center gap-2 text-gray-400">
              <span>o</span>
            </div>

            <div className="w-full sm:w-auto">
              <Button
                id="tour-sheets-new"
                onClick={createNewSheet}
                disabled={creatingSheet}
                variant="default"
                size="lg"
                className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-8 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95 font-semibold w-full sm:w-[350px] justify-center"
              >
                {creatingSheet ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Crear Nueva Hoja
                  </>
                )}
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Puedes seleccionar múltiples hojas a la vez o crear una nueva directamente
          </p>
        </div>

        {/* Lista de hojas */}
        {sheets.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Hojas disponibles
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedIds.size} de {sheets.filter(s => s.status !== 'connected').length} seleccionada(s)
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={selectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Seleccionar todas
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Limpiar
                </button>
              </div>
            </div>

            {/* Grid de hojas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
              {sheets.map((sheet, idx) => {
                const isSelected = selectedIds.has(sheet.sheet_id);
                const isConnected = sheet.status === 'connected';

                return (
                  <div
                    key={sheet.sheet_id}
                    id={idx === 0 ? 'tour-sheets-grid-first' : undefined}
                    className={`relative flex items-start gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${isConnected
                      ? 'border-green-300 bg-green-50 cursor-default'
                      : isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    onClick={() => !isConnected && toggleSelection(sheet.sheet_id)}
                  >
                    {/* Checkbox visual */}
                    <div className={`flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center mt-0.5 ${isConnected
                      ? 'border-green-500 bg-green-500'
                      : isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                      }`}>
                      {(isSelected || isConnected) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>

                    {/* Info de la hoja */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{sheet.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {sheet.owner || 'Propietario desconocido'}
                      </p>
                      {isConnected && (
                        <span className="inline-flex items-center text-xs text-green-600 font-medium mt-1">
                          <Check className="w-3 h-3 mr-1" />
                          Conectada
                        </span>
                      )}
                    </div>

                    {/* Enlace externo */}
                    <a
                      href={sheet.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Abrir en Google Sheets"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Acciones */}
            <div className="mt-6 pt-6 border-t border-gray-100 flex justify-between items-center">
              <button
                onClick={goBack}
                className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>

              <button
                id="tour-sheets-connect-btn"
                onClick={connectSelectedSheets}
                disabled={selectedIds.size === 0 || connecting}
                className={`px-6 py-2.5 rounded-xl text-white flex items-center gap-2 font-medium transition-all ${selectedIds.size === 0 || connecting
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] shadow-lg hover:shadow-xl'
                  }`}
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Conectar {selectedIds.size > 0 ? `${selectedIds.size} hoja(s)` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Estado vacío */}
        {sheets.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <FileSpreadsheet className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 mb-2">
              No hay hojas seleccionadas aún
            </p>
            <p className="text-sm text-gray-400">
              Usa el botón de arriba para agregar hojas desde Google Drive
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSheetsSelection;
